import { useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { CardElement, useElements, useStripe } from "@stripe/react-stripe-js";

import PageHeader from "../../components/PageHeader/PageHeader";
import useGuestCart from "../../hook/useGuestCart";
import { saveGuestCheckoutSnapshot } from "../../utils/guestCart";
import { useGetCartQuery } from "../../features/api/cartApiSlice";
import {
  useValidateCouponMutation,
  useGetCheckoutQuoteMutation,
  useCreatePaymentIntentMutation,
} from "../../features/api/checkoutApiSlice";

import "./checkoutForm.scss";

const US_STATES = [
  ["AL", "Alabama"],
  ["AK", "Alaska"],
  ["AZ", "Arizona"],
  ["AR", "Arkansas"],
  ["CA", "California"],
  ["CO", "Colorado"],
  ["CT", "Connecticut"],
  ["DE", "Delaware"],
  ["DC", "District of Columbia"],
  ["FL", "Florida"],
  ["GA", "Georgia"],
  ["HI", "Hawaii"],
  ["ID", "Idaho"],
  ["IL", "Illinois"],
  ["IN", "Indiana"],
  ["IA", "Iowa"],
  ["KS", "Kansas"],
  ["KY", "Kentucky"],
  ["LA", "Louisiana"],
  ["ME", "Maine"],
  ["MD", "Maryland"],
  ["MA", "Massachusetts"],
  ["MI", "Michigan"],
  ["MN", "Minnesota"],
  ["MS", "Mississippi"],
  ["MO", "Missouri"],
  ["MT", "Montana"],
  ["NE", "Nebraska"],
  ["NV", "Nevada"],
  ["NH", "New Hampshire"],
  ["NJ", "New Jersey"],
  ["NM", "New Mexico"],
  ["NY", "New York"],
  ["NC", "North Carolina"],
  ["ND", "North Dakota"],
  ["OH", "Ohio"],
  ["OK", "Oklahoma"],
  ["OR", "Oregon"],
  ["PA", "Pennsylvania"],
  ["RI", "Rhode Island"],
  ["SC", "South Carolina"],
  ["SD", "South Dakota"],
  ["TN", "Tennessee"],
  ["TX", "Texas"],
  ["UT", "Utah"],
  ["VT", "Vermont"],
  ["VA", "Virginia"],
  ["WA", "Washington"],
  ["WV", "West Virginia"],
  ["WI", "Wisconsin"],
  ["WY", "Wyoming"],
];

const money = (amount) => `$${Number(amount || 0).toFixed(2)}`;

const getErrorMessage = (error) =>
  error?.data?.message || error?.message || "Unable to process your request.";

export default function CheckoutForm() {
  const navigate = useNavigate();
  const stripe = useStripe();
  const elements = useElements();

  const { accessToken, authChecked } = useSelector((state) => state.auth);

  const isAuthenticated = Boolean(accessToken);
  const guestCart = useGuestCart();

  const {
    data: cartData,
    isLoading: isCartLoading,
    isFetching: isCartFetching,
    isUninitialized,
  } = useGetCartQuery(undefined, {
    skip: !authChecked || !isAuthenticated,
    refetchOnMountOrArgChange: true,
  });

  const cartItems = useMemo(() => {
    if (!authChecked) return [];

    return isAuthenticated ? cartData?.items || [] : guestCart.items;
  }, [authChecked, isAuthenticated, cartData?.items, guestCart.items]);

  const isCheckoutLoading =
    !authChecked ||
    (isAuthenticated && (isUninitialized || isCartLoading || isCartFetching));

  const [validateCoupon, { isLoading: isValidatingCoupon }] =
    useValidateCouponMutation();

  const [getCheckoutQuote, { isLoading: isCalculatingQuote }] =
    useGetCheckoutQuoteMutation();

  const [createPaymentIntent] = useCreatePaymentIntentMutation();

  const checkoutFormRef = useRef(null);
  const latestQuoteKeyRef = useRef("");

  const paymentBusyRef = useRef(false);

  // A checkout attempt keeps its ID even when a network
  // request fails. We do not silently create another
  // PaymentIntent after an uncertain result.
  const checkoutAttemptRef = useRef(null);

  const [quoteRecord, setQuoteRecord] = useState(null);
  const [quoteErrorRecord, setQuoteErrorRecord] = useState(null);

  const [paymentState, setPaymentState] = useState("idle");

  const [isCardComplete, setIsCardComplete] = useState(false);

  const [cardError, setCardError] = useState("");

  const [paymentError, setPaymentError] = useState("");

  const [paymentMessage, setPaymentMessage] = useState("");

  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [address, setAddress] = useState("");
  const [addressLine2, setAddressLine2] = useState("");
  const [city, setCity] = useState("");
  const [stateCode, setStateCode] = useState("");
  const [zipCode, setZipCode] = useState("");
  const [phone, setPhone] = useState("");

  const country = "US";

  const [couponInput, setCouponInput] = useState("");
  const [activeCoupon, setActiveCoupon] = useState(null);

  const [couponError, setCouponError] = useState("");

  const [couponSuccess, setCouponSuccess] = useState("");

  const paymentLocked =
    paymentState === "processing" ||
    paymentState === "review" ||
    paymentState === "succeeded";

  const checkoutInitialized = Boolean(checkoutAttemptRef.current?.initialized);

  // Only product identifiers and selections are sent.
  // The backend reads trusted prices and fulfillment
  // information from MongoDB.
  const checkoutRequest = {
    cartItems: cartItems.map((item) => ({
      productId: String(item.productId || ""),
      variantId: item.variantId ? String(item.variantId) : null,
      quantity: Number(item.quantity),
      size: item.size || "",
      color: item.color || "",
      volume: item.volume || "",
    })),
    email: email.trim(),
    fullName: fullName.trim(),
    address: address.trim(),
    addressLine2: addressLine2.trim(),
    city: city.trim(),
    state: stateCode,
    zipCode: zipCode.trim(),
    country,
    phone: phone.trim(),
    couponCode: activeCoupon?.code || "",
  };

  // The quote is valid only for the exact details
  // that were used to calculate it.
  const quoteKey = JSON.stringify(checkoutRequest);

  latestQuoteKeyRef.current = quoteKey;

  const validQuote = quoteRecord?.key === quoteKey ? quoteRecord.data : null;

  const quoteError =
    quoteErrorRecord?.key === quoteKey ? quoteErrorRecord.message : "";

  const handleCalculateQuote = async () => {
    if (paymentLocked || checkoutInitialized) return;

    if (!checkoutFormRef.current?.reportValidity()) {
      return;
    }

    const requestKey = quoteKey;

    setQuoteRecord(null);
    setQuoteErrorRecord(null);
    setPaymentError("");

    try {
      const result = await getCheckoutQuote(checkoutRequest).unwrap();

      if (latestQuoteKeyRef.current !== requestKey) {
        return;
      }

      if (!result?.breakdown || typeof result.breakdown.total !== "number") {
        throw new Error("The server returned an incomplete checkout quote.");
      }

      setQuoteRecord({
        key: requestKey,
        data: result,
      });
    } catch (error) {
      if (latestQuoteKeyRef.current !== requestKey) {
        return;
      }

      setQuoteErrorRecord({
        key: requestKey,
        message:
          getErrorMessage(error) || "Unable to calculate shipping and tax.",
      });
    }
  };

  // Merchandise-only estimate before the server
  // calculates shipping and sales tax.
  const summary = useMemo(() => {
    const subtotal = cartItems.reduce(
      (sum, item) => sum + Number(item.price || 0) * Number(item.quantity || 0),
      0
    );

    let discount = 0;

    if (activeCoupon?.type === "percentage") {
      discount = subtotal * (Number(activeCoupon.discountPercent || 0) / 100);
    } else if (activeCoupon?.type === "fixed_usd") {
      discount = Number(activeCoupon.discountPercent || 0);
    }

    discount = Math.min(subtotal, Math.max(0, discount));

    return {
      subtotal,
      discount,
      merchandiseEstimate: Math.max(0, subtotal - discount),
    };
  }, [cartItems, activeCoupon]);

  const handleApplyCoupon = async () => {
    if (paymentLocked || checkoutInitialized) return;

    setCouponError("");
    setCouponSuccess("");

    if (!couponInput.trim()) return;
    try {
      const result = await validateCoupon({
        code: couponInput.trim(),
      }).unwrap();

      setActiveCoupon(result);

      setCouponSuccess(`Coupon ${result.code} applied to your estimate.`);
    } catch (error) {
      setCouponError(error?.data?.message || "Invalid coupon code.");

      setActiveCoupon(null);
    }
  };

  const handleClearCoupon = () => {
    if (paymentLocked || checkoutInitialized) return;

    setActiveCoupon(null);
    setCouponInput("");
    setCouponError("");
    setCouponSuccess("");
  };

  // Payment confirmation happens through Stripe.
  // Paid Order creation happens through the webhook.
  const handleFinalOrderSubmit = async (event) => {
    event.preventDefault();

    if (paymentBusyRef.current || paymentLocked) {
      return;
    }

    setPaymentError("");
    setPaymentMessage("");

    if (!checkoutFormRef.current?.reportValidity()) {
      return;
    }

    if (!validQuote) {
      setPaymentError("Calculate shipping and tax before making a payment.");
      return;
    }

    if (!isCardComplete) {
      setPaymentError("Please complete your card information before paying.");
      return;
    }

    if (!stripe || !elements) {
      setPaymentError(
        "Secure card payments are still loading. Please try again."
      );
      return;
    }

    const card = elements.getElement(CardElement);

    if (!card) {
      setPaymentError(
        "Card details are not available. Please refresh the page."
      );
      return;
    }

    const requestKey = quoteKey;
    const existingAttempt = checkoutAttemptRef.current;

    if (existingAttempt && existingAttempt.key !== requestKey) {
      setPaymentState("review");
      setPaymentError(
        "A checkout attempt already exists for different details. Please contact support before starting another payment."
      );
      return;
    }

    const checkoutRequestId = existingAttempt?.id || crypto.randomUUID();

    checkoutAttemptRef.current = {
      id: checkoutRequestId,
      key: requestKey,
      initialized: false,
    };

    paymentBusyRef.current = true;
    setPaymentState("processing");

    try {
      const result = await createPaymentIntent({
        ...checkoutRequest,
        checkoutRequestId,
      }).unwrap();

      checkoutAttemptRef.current.initialized = true;

      if (
        !result?.clientSecret ||
        !result?.paymentIntentId ||
        result.checkoutRequestId !== checkoutRequestId
      ) {
        throw new Error(
          "The payment response could not be verified. Do not retry until the checkout is reviewed."
        );
      }

      // A confirmation reference is required before charging the card.
      // Without it, we cannot safely route to the verified order page.
      if (
        typeof result.checkoutSessionId !== "string" ||
        !/^[a-f\d]{24}$/i.test(result.checkoutSessionId)
      ) {
        setPaymentState("review");
        setPaymentError(
          "The server did not provide a valid checkout reference. No card confirmation was attempted. Please contact support before retrying."
        );
        return;
      }

      // The browser displays the quote, but the server
      // independently recalculates the payment amount.
      const quotedCents = Math.round(validQuote.breakdown.total * 100);

      if (
        !Number.isSafeInteger(result.amountCents) ||
        result.amountCents !== quotedCents
      ) {
        setPaymentState("review");

        setPaymentError(
          `The final total is ${money(
            result.amountCents / 100
          )}, different from your displayed quote of ${money(
            validQuote.breakdown.total
          )}. No card confirmation was attempted. This checkout requires review before another payment attempt.`
        );

        return;
      }

      if (latestQuoteKeyRef.current !== requestKey) {
        setPaymentState("review");

        setPaymentError(
          "Your checkout details changed during payment initialization. No card confirmation was attempted. Please contact support."
        );

        return;
      }

      // Record the exact submitted guest cart before charging the card.
      // A repeated checkout session retains its original snapshot.
      if (!isAuthenticated) {
        try {
          saveGuestCheckoutSnapshot(
            result.checkoutSessionId,
            checkoutRequest.cartItems
          );
        } catch (snapshotError) {
          setPaymentState("review");
          setPaymentError(
            "We could not safely record your checkout cart. No card confirmation was attempted. Please check browser storage and contact support before retrying."
          );
          return;
        }
      }

      const { error, paymentIntent } = await stripe.confirmCardPayment(
        result.clientSecret,
        {
          payment_method: {
            card,
            billing_details: {
              name: fullName.trim(),
              email: email.trim(),
              address: {
                line1: address.trim(),
                line2: addressLine2.trim() || undefined,
                city: city.trim(),
                state: stateCode,
                postal_code: zipCode.trim(),
                country: "US",
              },
            },
          },
        }
      );

      if (error) {
        setPaymentState("review");

        setPaymentError(
          `${
            error.message || "Stripe could not confirm the card."
          } Please check your payment status before attempting another checkout.`
        );

        return;
      }

      if (paymentIntent?.id !== result.paymentIntentId) {
        throw new Error(
          "Stripe returned an unexpected payment reference. Please contact support."
        );
      }

      if (paymentIntent.status === "succeeded") {
        setPaymentState("succeeded");

        setPaymentMessage(
          `Payment confirmed by Stripe. Reference: ${paymentIntent.id}. Your order confirmation is being processed.`
        );

        // Do not clear the cart here. The confirmation page must
        // independently verify the paid Order created by the webhook.
        navigate(
          `/checkout/confirmation?checkoutSessionId=${encodeURIComponent(
            result.checkoutSessionId
          )}`,
          { replace: true }
        );
      } else {
        setPaymentState("review");

        setPaymentMessage(
          `Payment status: ${paymentIntent.status}. Please check the payment status before retrying.`
        );
      }
    } catch (error) {
      setPaymentState("review");

      setPaymentError(
        `${getErrorMessage(
          error
        )} A payment attempt may exist. Do not start another checkout until its status is verified.`
      );
    } finally {
      paymentBusyRef.current = false;
    }
  };

  if (isCheckoutLoading) {
    return (
      <div
        className="product-detail-error"
        style={{
          padding: "10rem 2rem",
          textAlign: "center",
        }}
      >
        <h2>🔄 Syncing secure cart lanes...</h2>
      </div>
    );
  }

  if (cartItems.length === 0) {
    return (
      <div className="product-detail-error" style={{ padding: "10rem 2rem" }}>
        <h2>🛒 Your Basket is Empty</h2>
        <p>Add some branded travel gear before heading to checkout.</p>
        <Link to="/shop" className="back-shop-link">
          Return to Basecamp Shop
        </Link>
      </div>
    );
  }

  return (
    <main className="checkout-page fuel-fade-in">
      <div className="checkout-wrapper">
        <PageHeader
          kicker="Secure Checkout"
          title="Finalize Your Order"
          subtitle="Enter your delivery details. Shipping and taxes will be calculated before payment."
        />

        <div className="checkout-grid-layout">
          <form
            className="checkout-form-panel"
            ref={checkoutFormRef}
            onSubmit={handleFinalOrderSubmit}
          >
            <h3>📋 Contact & Delivery Information</h3>

            <div className="checkout-fields-stack">
              <label className="checkout-input-row">
                Email Address *
                <input
                  disabled={paymentLocked}
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="driver@example.com"
                  required
                />
              </label>

              <label className="checkout-input-row">
                Full Name *
                <input
                  disabled={paymentLocked}
                  type="text"
                  autoComplete="name"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder="Recipient's full name"
                  required
                />
              </label>

              <label className="checkout-input-row">
                Country *
                <select value={country} disabled aria-label="Shipping country">
                  <option value="US">United States</option>
                </select>
              </label>

              <label className="checkout-input-row">
                Street Address *
                <input
                  disabled={paymentLocked}
                  type="text"
                  autoComplete="address-line1"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  placeholder="Street number and street name"
                  required
                />
              </label>

              <label className="checkout-input-row">
                Apartment, Suite, Unit (optional)
                <input
                  disabled={paymentLocked}
                  type="text"
                  autoComplete="address-line2"
                  value={addressLine2}
                  onChange={(e) => setAddressLine2(e.target.value)}
                  placeholder="Apt 4B, Suite 200, etc."
                />
              </label>

              <div className="fields-inline-split-row">
                <label className="checkout-input-row">
                  City *
                  <input
                    disabled={paymentLocked}
                    type="text"
                    autoComplete="address-level2"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    placeholder="City"
                    required
                  />
                </label>

                <label className="checkout-input-row">
                  State *
                  <select
                    disabled={paymentLocked}
                    autoComplete="address-level1"
                    value={stateCode}
                    onChange={(e) => setStateCode(e.target.value)}
                    required
                  >
                    <option value="">Select state</option>
                    {US_STATES.map(([code, label]) => (
                      <option key={code} value={code}>
                        {label}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              <div className="fields-inline-split-row">
                <label className="checkout-input-row">
                  ZIP Code *
                  <input
                    disabled={paymentLocked}
                    type="text"
                    inputMode="numeric"
                    autoComplete="postal-code"
                    pattern="[0-9]{5}(-[0-9]{4})?"
                    title="Enter a 5-digit ZIP code or ZIP+4"
                    value={zipCode}
                    onChange={(e) => setZipCode(e.target.value)}
                    placeholder="50111"
                    required
                  />
                </label>

                <label className="checkout-input-row">
                  Phone (optional)
                  <input
                    disabled={paymentLocked}
                    type="tel"
                    autoComplete="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="For delivery questions"
                  />
                </label>
              </div>
            </div>

            <h3 style={{ marginTop: "2.5rem" }}>🚚 Shipping & Delivery</h3>

            <div className="shipping-methods-selector-stack">
              <div className="shipping-method-option-card" role="status">
                <div className="option-txt">
                  <strong>
                    {validQuote
                      ? "Shipping calculated"
                      : "Shipping calculated at checkout"}
                  </strong>

                  <span>
                    {validQuote
                      ? `Method: ${
                          validQuote.shipping?.method || "Available shipping"
                        }`
                      : "Enter your delivery details, then calculate shipping and tax."}
                  </span>
                </div>

                <span className="price-tag-inline">
                  {validQuote
                    ? money(validQuote.breakdown.shipping)
                    : "Pending"}
                </span>
              </div>
            </div>

            <button
              type="button"
              className="fuel-primary-btn"
              onClick={handleCalculateQuote}
              disabled={
                isCalculatingQuote ||
                isCheckoutLoading ||
                paymentLocked ||
                checkoutInitialized
              }
              style={{ marginTop: "1rem" }}
            >
              {isCalculatingQuote
                ? "Calculating Shipping & Tax..."
                : validQuote
                ? "Recalculate Shipping & Tax"
                : "Calculate Shipping & Tax"}
            </button>

            {quoteError && (
              <div
                role="alert"
                className="notes-alert notes-alert--error"
                style={{ marginTop: "1rem" }}
              >
                {quoteError}
              </div>
            )}

            {validQuote && (
              <p
                role="status"
                style={{
                  marginTop: "0.75rem",
                  fontSize: "0.85rem",
                }}
              >
                Shipping and tax estimate ready. Review the order breakdown.
              </p>
            )}

            <h3 style={{ marginTop: "2.5rem" }}>💳 Credit Card Information</h3>

            <div
              className="stripe-card-input-wrapper"
              style={{
                padding: "1rem",
                background: "#111",
                borderRadius: "6px",
                border: "1px solid #333",
                marginBottom: "1.5rem",
              }}
            >
              <CardElement
                onChange={(event) => {
                  setIsCardComplete(event.complete);
                  setCardError(event.error?.message || "");
                }}
                options={{
                  disabled: paymentLocked,
                  style: {
                    base: {
                      fontSize: "16px",
                      color: "#fff",
                      "::placeholder": {
                        color: "#666",
                      },
                    },
                    invalid: {
                      color: "#ff4d4d",
                    },
                  },
                }}
              />
            </div>

            <p
              style={{
                fontSize: "0.85rem",
                opacity: 0.8,
                marginBottom: "1rem",
              }}
            >
              Your payment is processed securely by Stripe. During sandbox
              testing, use a Stripe test card; no real funds are charged.
            </p>

            <button
              type="submit"
              className="fuel-primary-btn process-checkout-submit-btn"
              disabled={
                !validQuote ||
                !isCardComplete ||
                !stripe ||
                !elements ||
                paymentLocked ||
                isCalculatingQuote
              }
            >
              {paymentState === "processing"
                ? "Processing Payment..."
                : paymentState === "succeeded"
                ? "Payment Confirmed"
                : paymentState === "review"
                ? "Payment Requires Review"
                : `🔒 Pay ${money(validQuote?.breakdown?.total || 0)} Securely`}
            </button>

            {paymentError && (
              <div
                role="alert"
                className="notes-alert notes-alert--error"
                style={{ marginTop: "1rem" }}
              >
                {paymentError}
              </div>
            )}

            {paymentMessage && (
              <div
                role="status"
                className="notes-alert notes-alert--success"
                style={{ marginTop: "1rem" }}
              >
                {paymentMessage}
              </div>
            )}
          </form>

          <aside className="checkout-summary-panel">
            <h3>📦 Order Breakdown</h3>

            <div className="summary-products-scroll-stack">
              {cartItems.map((item, index) => {
                const options = item.selectedOptions || {};

                const hasOptions =
                  item.fulfillmentMethod === "printify" &&
                  Object.keys(options).length > 0;

                return (
                  <div
                    key={
                      item._id ||
                      `${item.productId}-${
                        item.variantId ||
                        `${item.size}-${item.color}-${item.volume}`
                      }-${index}`
                    }
                    className="summary-item-chip-row"
                  >
                    <img src={item.image} alt="" className="thumb-round" />

                    <div className="meta-txt">
                      <strong>{item.name}</strong>

                      <span>Qty: {item.quantity}</span>

                      {hasOptions ? (
                        Object.entries(options).map(([name, value]) => (
                          <span key={name}>
                            {name}: {String(value)}
                          </span>
                        ))
                      ) : (
                        <span>
                          {[
                            item.size && `Size: ${item.size}`,
                            item.color && `Color: ${item.color}`,
                            item.volume && `Volume: ${item.volume}`,
                          ]
                            .filter(Boolean)
                            .join(" | ")}
                        </span>
                      )}
                    </div>

                    <span className="line-price">
                      {money(
                        Number(item.price || 0) * Number(item.quantity || 0)
                      )}
                    </span>
                  </div>
                );
              })}
            </div>

            <div className="coupon-redemption-wrapper">
              <div
                className="add-color-action-row"
                style={{
                  display: "flex",
                  gap: "0.5rem",
                }}
              >
                <input
                  type="text"
                  placeholder="PROMO CODE"
                  value={couponInput}
                  onChange={(e) => setCouponInput(e.target.value)}
                  className="store-search-field"
                  style={{
                    margin: 0,
                    padding: "0.6rem",
                    flex: 1,
                  }}
                  disabled={
                    Boolean(activeCoupon) ||
                    paymentLocked ||
                    checkoutInitialized
                  }
                />

                {activeCoupon ? (
                  <button
                    type="button"
                    className="fuel-primary-btn clear-coupon-btn"
                    onClick={handleClearCoupon}
                    disabled={paymentLocked || checkoutInitialized}
                  >
                    Clear
                  </button>
                ) : (
                  <button
                    type="button"
                    className="fuel-primary-btn"
                    disabled={
                      isValidatingCoupon || paymentLocked || checkoutInitialized
                    }
                    onClick={handleApplyCoupon}
                  >
                    {isValidatingCoupon ? "Checking..." : "Apply"}
                  </button>
                )}
              </div>

              {couponError && (
                <div role="alert" className="notes-alert notes-alert--error">
                  {couponError}
                </div>
              )}

              {couponSuccess && (
                <div role="status" className="notes-alert notes-alert--success">
                  {couponSuccess}
                </div>
              )}
            </div>

            <div className="summary-receipt-totals-chassis">
              <div className="receipt-row">
                <span>Basket Subtotal:</span>

                <span>
                  {money(
                    validQuote
                      ? validQuote.breakdown.subtotal
                      : summary.subtotal
                  )}
                </span>
              </div>

              {(validQuote
                ? validQuote.breakdown.discount > 0
                : summary.discount > 0) && (
                <div className="receipt-row receipt-row--discount">
                  <span>Coupon Savings:</span>

                  <span>
                    -
                    {money(
                      validQuote
                        ? validQuote.breakdown.discount
                        : summary.discount
                    )}
                  </span>
                </div>
              )}

              <div className="receipt-row">
                <span>Shipping:</span>

                <span>
                  {validQuote
                    ? money(validQuote.breakdown.shipping)
                    : "To be calculated"}
                </span>
              </div>

              <div className="receipt-row">
                <span>Estimated Tax:</span>

                <span>
                  {validQuote
                    ? money(validQuote.breakdown.tax)
                    : "To be calculated"}
                </span>
              </div>

              <hr
                style={{
                  opacity: 0.1,
                  margin: "1rem 0",
                  border: "none",
                  borderTop: "1px solid currentColor",
                }}
              />

              <div className="receipt-row receipt-row--grand">
                <span>
                  {validQuote
                    ? "Estimated Order Total:"
                    : "Merchandise Estimate:"}
                </span>

                <strong>
                  {money(
                    validQuote
                      ? validQuote.breakdown.total
                      : summary.merchandiseEstimate
                  )}
                </strong>
              </div>

              <p
                style={{
                  fontSize: "0.8rem",
                  opacity: 0.7,
                  marginTop: "0.75rem",
                }}
              >
                {validQuote
                  ? "Server-calculated estimate. The final amount will be recalculated before payment."
                  : "Not a final total. Calculate shipping and tax to see the estimated order total."}
              </p>
            </div>
          </aside>
        </div>
      </div>
    </main>
  );
}
